import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

// Add auth token to requests
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface Referral {
  id: string;
  referrerId: string;
  referredId: string;
  referralCode: string;
  status: 'PENDING' | 'ACTIVE' | 'QUALIFIED' | 'PAID';
  signupDate: Date;
  qualifiedDate?: Date;
  paidDate?: Date;
  revenueGenerated: number;
  commission: number;
  commissionRate: number;
  createdAt: Date;
  updatedAt: Date;
  referrer?: {
    id: string;
    username: string;
    email: string;
  };
  referred?: {
    id: string;
    username: string;
    email: string;
  };
}

export interface ReferralActivity {
  id: string;
  referralId: string;
  activityType: string;
  metadata?: any;
  createdAt: Date;
}

export interface ReferralStats {
  total: number;
  active: number;
  qualified: number;
  paid: number;
  totalRevenue: number;
  totalCommission: number;
  conversionRate: number;
}

class ReferralService {
  /**
   * Get referrals for a creator/referrer
   */
  async getReferrals(referrerId?: string): Promise<Referral[]> {
    const params = referrerId ? { referrerId } : {};
    const response = await axios.get(`${API_URL}/api/referrals`, {
      params,
      headers: getAuthHeaders(),
    });
    return response.data.referrals;
  }

  /**
   * Get a single referral by ID
   */
  async getReferral(referralId: string): Promise<Referral> {
    const response = await axios.get(`${API_URL}/api/referrals/${referralId}`, {
      headers: getAuthHeaders(),
    });
    return response.data.referral;
  }

  /**
   * Get referral by code
   */
  async getReferralByCode(referralCode: string): Promise<Referral | null> {
    const response = await axios.get(`${API_URL}/api/referrals/code/${referralCode}`, {
      headers: getAuthHeaders(),
    });
    return response.data.referral;
  }

  /**
   * Create a new referral
   */
  async createReferral(referrerId: string, referredId: string, referralCode: string): Promise<Referral> {
    const response = await axios.post(
      `${API_URL}/api/referrals`,
      { referrerId, referredId, referralCode },
      { headers: getAuthHeaders() }
    );
    return response.data.referral;
  }

  /**
   * Update referral status
   */
  async updateReferralStatus(
    referralId: string,
    status: 'PENDING' | 'ACTIVE' | 'QUALIFIED' | 'PAID'
  ): Promise<Referral> {
    const response = await axios.put(
      `${API_URL}/api/referrals/${referralId}/status`,
      { status },
      { headers: getAuthHeaders() }
    );
    return response.data.referral;
  }

  /**
   * Track referral activity
   */
  async trackActivity(referralId: string, activityType: string, metadata?: any): Promise<ReferralActivity> {
    const response = await axios.post(
      `${API_URL}/api/referrals/${referralId}/activity`,
      { activityType, metadata },
      { headers: getAuthHeaders() }
    );
    return response.data.activity;
  }

  /**
   * Calculate and update commission
   */
  async calculateCommission(referralId: string, revenue: number): Promise<Referral> {
    const response = await axios.post(
      `${API_URL}/api/referrals/${referralId}/commission`,
      { revenue },
      { headers: getAuthHeaders() }
    );
    return response.data.referral;
  }

  /**
   * Mark referral as paid
   */
  async markAsPaid(referralId: string): Promise<Referral> {
    const response = await axios.post(
      `${API_URL}/api/referrals/${referralId}/mark-paid`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data.referral;
  }

  /**
   * Get referral statistics
   */
  async getStats(referrerId?: string): Promise<ReferralStats> {
    const params = referrerId ? { referrerId } : {};
    const response = await axios.get(`${API_URL}/api/referrals/stats`, {
      params,
      headers: getAuthHeaders(),
    });
    return response.data.stats;
  }

  /**
   * Get current user's referral code
   */
  async getMyReferralCode(): Promise<string> {
    const response = await axios.get(`${API_URL}/api/referrals/my-code`, {
      headers: getAuthHeaders(),
    });
    return response.data.referralCode;
  }
}

export default new ReferralService();
